import { toDelimited } from "@/lib/tool-kit/text/delimited";

/**
 * The data model behind the qualitative coding tool: documents, a nested codebook, and
 * coded passages (segments) that point into a document by character offset.
 *
 * Everything here is a pure function over a plain JSON-able `Project`, so the widget can
 * keep it in React state, save it to localStorage and write it to a backup file with the
 * same shape. A document's text never changes after it is added: offsets stay valid,
 * which is why there is no "edit text" operation.
 */

const RE_SPACE = /\s/;

export interface CodingDocument {
  id: string;
  title: string;
  text: string;
}

export interface Code {
  id: string;
  name: string;
  parentId: string | null;
}

export interface Segment {
  id: string;
  docId: string;
  start: number;
  end: number;
  codeId: string;
  memo: string;
}

export interface Project {
  version: 1;
  /** Counter behind every id, so ids are deterministic and never reused. */
  next: number;
  documents: CodingDocument[];
  codes: Code[];
  segments: Segment[];
}

export function emptyProject(): Project {
  return { version: 1, next: 1, documents: [], codes: [], segments: [] };
}

function issueId(project: Project, prefix: string): [Project, string] {
  return [{ ...project, next: project.next + 1 }, `${prefix}${project.next}`];
}

export function addDocument(
  project: Project,
  title: string,
  text: string
): { project: Project; id: string } {
  const [next, id] = issueId(project, "d");
  return {
    project: { ...next, documents: [...next.documents, { id, title, text }] },
    id,
  };
}

export function removeDocument(project: Project, docId: string): Project {
  return {
    ...project,
    documents: project.documents.filter((doc) => doc.id !== docId),
    segments: project.segments.filter((seg) => seg.docId !== docId),
  };
}

export function addCode(
  project: Project,
  name: string,
  parentId: string | null
): { project: Project; id: string | null } {
  const clean = name.trim();
  const parent = parentId ?? null;
  const duplicate = project.codes.some(
    (code) =>
      code.parentId === parent &&
      code.name.toLowerCase() === clean.toLowerCase()
  );
  if (clean === "" || duplicate) {
    return { project, id: null };
  }
  const [next, id] = issueId(project, "c");
  return {
    project: {
      ...next,
      codes: [...next.codes, { id, name: clean, parentId: parent }],
    },
    id,
  };
}

export function renameCode(
  project: Project,
  codeId: string,
  name: string
): Project {
  const clean = name.trim();
  const target = project.codes.find((code) => code.id === codeId);
  if (!target || clean === "") {
    return project;
  }
  const taken = project.codes.some(
    (code) =>
      code.id !== codeId &&
      code.parentId === target.parentId &&
      code.name.toLowerCase() === clean.toLowerCase()
  );
  if (taken) {
    return project;
  }
  return {
    ...project,
    codes: project.codes.map((code) =>
      code.id === codeId ? { ...code, name: clean } : code
    ),
  };
}

/** A code and every code beneath it. */
export function subtreeIds(project: Project, codeId: string): string[] {
  const ids = [codeId];
  for (const id of ids) {
    for (const code of project.codes) {
      if (code.parentId === id && !ids.includes(code.id)) {
        ids.push(code.id);
      }
    }
  }
  return ids;
}

/** Remove a code, its sub-codes, and every passage coded with any of them. */
export function removeCode(project: Project, codeId: string): Project {
  const gone = new Set(subtreeIds(project, codeId));
  return {
    ...project,
    codes: project.codes.filter((code) => !gone.has(code.id)),
    segments: project.segments.filter((seg) => !gone.has(seg.codeId)),
  };
}

/** Pull a selection in from any leading or trailing whitespace. */
export function trimRange(
  text: string,
  start: number,
  end: number
): { start: number; end: number } | null {
  let from = Math.max(0, Math.min(start, end));
  let to = Math.min(text.length, Math.max(start, end));
  while (from < to && RE_SPACE.test(text[from] ?? "")) {
    from++;
  }
  while (to > from && RE_SPACE.test(text[to - 1] ?? "")) {
    to--;
  }
  return to > from ? { start: from, end: to } : null;
}

export function addSegment(
  project: Project,
  docId: string,
  start: number,
  end: number,
  codeId: string
): Project {
  const doc = project.documents.find((item) => item.id === docId);
  const range = doc ? trimRange(doc.text, start, end) : null;
  if (!(range && project.codes.some((code) => code.id === codeId))) {
    return project;
  }
  const same = project.segments.some(
    (seg) =>
      seg.docId === docId &&
      seg.codeId === codeId &&
      seg.start === range.start &&
      seg.end === range.end
  );
  if (same) {
    return project;
  }
  const [next, id] = issueId(project, "s");
  return {
    ...next,
    segments: [
      ...next.segments,
      { id, docId, start: range.start, end: range.end, codeId, memo: "" },
    ],
  };
}

export function removeSegment(project: Project, segmentId: string): Project {
  return {
    ...project,
    segments: project.segments.filter((seg) => seg.id !== segmentId),
  };
}

export function setMemo(
  project: Project,
  segmentId: string,
  memo: string
): Project {
  return {
    ...project,
    segments: project.segments.map((seg) =>
      seg.id === segmentId ? { ...seg, memo } : seg
    ),
  };
}

/** "Barriers > Cost": the code's name behind its ancestors'. */
export function codePath(
  project: Project,
  codeId: string,
  separator = " > "
): string {
  const names: string[] = [];
  let current = project.codes.find((code) => code.id === codeId);
  const seen = new Set<string>();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.unshift(current.name);
    const parentId: string | null = current.parentId;
    current = parentId
      ? project.codes.find((code) => code.id === parentId)
      : undefined;
  }
  return names.join(separator);
}

/** Codes in tree order (each parent followed by its children), with their depth. */
export function codeTree(project: Project): { code: Code; depth: number }[] {
  const rows: { code: Code; depth: number }[] = [];
  const visit = (parentId: string | null, depth: number) => {
    const children = project.codes
      .filter((code) => code.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name));
    for (const code of children) {
      rows.push({ code, depth });
      visit(code.id, depth + 1);
    }
  };
  visit(null, 0);
  return rows;
}

export interface CodeCount {
  /** Passages coded with exactly this code. */
  direct: number;
  /** Passages coded with this code or any sub-code. */
  total: number;
}

export function codeCounts(project: Project): Map<string, CodeCount> {
  const direct = new Map<string, number>();
  for (const seg of project.segments) {
    direct.set(seg.codeId, (direct.get(seg.codeId) ?? 0) + 1);
  }
  const counts = new Map<string, CodeCount>();
  for (const code of project.codes) {
    const total = subtreeIds(project, code.id).reduce(
      (sum, id) => sum + (direct.get(id) ?? 0),
      0
    );
    counts.set(code.id, { direct: direct.get(code.id) ?? 0, total });
  }
  return counts;
}

export interface Piece {
  start: number;
  end: number;
  /** The passages that cover this stretch, in the order they were made. */
  segmentIds: string[];
}

/**
 * Cut a document into runs where the set of covering passages is constant, so
 * overlapping passages can be drawn without nesting marks.
 */
export function documentPieces(
  textLength: number,
  segments: readonly Segment[]
): Piece[] {
  const points = new Set<number>([0, textLength]);
  for (const seg of segments) {
    points.add(Math.max(0, seg.start));
    points.add(Math.min(textLength, seg.end));
  }
  const sorted = [...points].sort((a, b) => a - b);
  const pieces: Piece[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i] ?? 0;
    const end = sorted[i + 1] ?? 0;
    pieces.push({
      start,
      end,
      segmentIds: segments
        .filter((seg) => seg.start <= start && seg.end >= end)
        .map((seg) => seg.id),
    });
  }
  return pieces;
}

/** Characters covered by at least one passage, over the document's length. */
export function coverage(
  project: Project,
  docId?: string
): { coded: number; total: number } {
  let coded = 0;
  let total = 0;
  for (const doc of project.documents) {
    if (docId && doc.id !== docId) {
      continue;
    }
    total += doc.text.length;
    const ranges = project.segments
      .filter((seg) => seg.docId === doc.id)
      .map((seg) => [seg.start, seg.end] as const)
      .sort((a, b) => a[0] - b[0]);
    let cursor = 0;
    for (const [start, end] of ranges) {
      const from = Math.max(start, cursor);
      if (end > from) {
        coded += end - from;
        cursor = end;
      }
    }
  }
  return { coded, total };
}

export interface Excerpt {
  segment: Segment;
  document: CodingDocument;
  path: string;
  text: string;
}

/** Passages for one code (with its sub-codes, if asked) or for all codes, in reading order. */
export function excerptsFor(
  project: Project,
  codeId: string | null,
  includeChildren: boolean
): Excerpt[] {
  const wanted = codeId
    ? new Set(includeChildren ? subtreeIds(project, codeId) : [codeId])
    : null;
  const rows: Excerpt[] = [];
  for (const segment of project.segments) {
    const document = project.documents.find((doc) => doc.id === segment.docId);
    if (!document || (wanted && !wanted.has(segment.codeId))) {
      continue;
    }
    rows.push({
      segment,
      document,
      path: codePath(project, segment.codeId),
      text: document.text.slice(segment.start, segment.end),
    });
  }
  const docOrder = new Map(project.documents.map((doc, i) => [doc.id, i]));
  return rows.sort(
    (a, b) =>
      a.path.localeCompare(b.path) ||
      (docOrder.get(a.document.id) ?? 0) - (docOrder.get(b.document.id) ?? 0) ||
      a.segment.start - b.segment.start
  );
}

const RE_LINE_BREAKS = /\s*[\r\n]+\s*/g;

function oneLine(text: string): string {
  return text.replace(RE_LINE_BREAKS, " ");
}

/** The excerpts as CSV: one passage per row, ready for a spreadsheet. */
export function excerptsCsv(
  rows: readonly Excerpt[],
  head: readonly [string, string, string, string, string, string]
): string {
  return toDelimited(
    [
      [...head],
      ...rows.map((row) => [
        row.document.title,
        row.path,
        row.text,
        String(row.segment.start + 1),
        String(row.segment.end),
        row.segment.memo,
      ]),
    ],
    ",",
    "always"
  );
}

/** The codebook with counts, as CSV. */
export function codebookCsv(
  project: Project,
  head: readonly [string, string, string, string]
): string {
  const counts = codeCounts(project);
  return toDelimited(
    [
      [...head],
      ...codeTree(project).map(({ code }) => [
        codePath(project, code.id),
        project.codes.find((c) => c.id === code.parentId)?.name ?? "",
        String(counts.get(code.id)?.direct ?? 0),
        String(counts.get(code.id)?.total ?? 0),
      ]),
    ],
    ",",
    "always"
  );
}

export interface MarkdownLabels {
  title: string;
  characters: string;
  noExcerpts: string;
}

/** Excerpts grouped under their code, nested codes as nested headings. */
export function excerptsMarkdown(
  project: Project,
  labels: MarkdownLabels
): string {
  const lines = [`# ${labels.title}`, ""];
  const counts = codeCounts(project);
  for (const { code, depth } of codeTree(project)) {
    const level = Math.min(6, depth + 2);
    lines.push(
      `${"#".repeat(level)} ${code.name} (${counts.get(code.id)?.direct ?? 0})`,
      ""
    );
    const own = excerptsFor(project, code.id, false);
    if (own.length === 0) {
      lines.push(`_${labels.noExcerpts}_`, "");
    }
    for (const row of own) {
      lines.push(`> ${oneLine(row.text)}`, "");
      const where = `${row.document.title}, ${labels.characters} ${row.segment.start + 1}–${row.segment.end}`;
      lines.push(
        row.segment.memo
          ? `— ${where}. ${oneLine(row.segment.memo)}`
          : `— ${where}`,
        ""
      );
    }
  }
  return `${lines.join("\n").trimEnd()}\n`;
}

function reachesRoot(codes: readonly Code[], start: Code): boolean {
  let current: Code | undefined = start;
  for (let step = 0; step <= codes.length; step++) {
    if (!current?.parentId) {
      return true;
    }
    const parentId: string = current.parentId;
    current = codes.find((code) => code.id === parentId);
  }
  return false;
}

export function serializeProject(project: Project): string {
  return JSON.stringify(project, null, 2);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function parseDocuments(raw: unknown): CodingDocument[] {
  const documents: CodingDocument[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const id = isRecord(item) ? str(item.id) : null;
    const title = isRecord(item) ? str(item.title) : null;
    const text = isRecord(item) ? str(item.text) : null;
    if (id && title !== null && text !== null) {
      documents.push({ id, title, text });
    }
  }
  return documents;
}

function parseCodes(raw: unknown): Code[] {
  const codes: Code[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    const id = isRecord(item) ? str(item.id) : null;
    const name = isRecord(item) ? str(item.name) : null;
    const parentId = isRecord(item) ? str(item.parentId) : null;
    if (id && name) {
      codes.push({ id, name, parentId });
    }
  }
  const ids = new Set(codes.map((code) => code.id));
  const linked = codes.map((code) => ({
    ...code,
    parentId: code.parentId && ids.has(code.parentId) ? code.parentId : null,
  }));
  return linked.map((code) =>
    reachesRoot(linked, code) ? code : { ...code, parentId: null }
  );
}

function isValidRange(start: unknown, end: unknown, length: number): boolean {
  return (
    typeof start === "number" &&
    typeof end === "number" &&
    Number.isInteger(start) &&
    Number.isInteger(end) &&
    start >= 0 &&
    end > start &&
    end <= length
  );
}

function parseSegments(
  raw: unknown,
  documents: readonly CodingDocument[],
  codes: readonly Code[]
): Segment[] {
  const codeIds = new Set(codes.map((code) => code.id));
  const segments: Segment[] = [];
  for (const item of Array.isArray(raw) ? raw : []) {
    if (!isRecord(item)) {
      continue;
    }
    const doc = documents.find((candidate) => candidate.id === item.docId);
    if (
      doc &&
      typeof item.id === "string" &&
      typeof item.codeId === "string" &&
      codeIds.has(item.codeId) &&
      isValidRange(item.start, item.end, doc.text.length)
    ) {
      segments.push({
        id: item.id,
        docId: doc.id,
        start: item.start as number,
        end: item.end as number,
        codeId: item.codeId,
        memo: typeof item.memo === "string" ? item.memo : "",
      });
    }
  }
  return segments;
}

/**
 * Read a saved or downloaded project back in. Anything malformed is dropped rather than
 * trusted: a bad passage must not be able to point outside its document.
 */
export function parseProject(source: string): Project | null {
  let data: unknown;
  try {
    data = JSON.parse(source);
  } catch {
    return null;
  }
  if (!(isRecord(data) && data.version === 1)) {
    return null;
  }
  const documents = parseDocuments(data.documents);
  const codes = parseCodes(data.codes);
  const segments = parseSegments(data.segments, documents, codes);
  const used = [...documents, ...codes, ...segments].map(
    (item) => Number(item.id.slice(1)) + 1
  );
  const declared = typeof data.next === "number" ? data.next : 1;
  return {
    version: 1,
    next: Math.max(declared, 1, ...used.filter(Number.isFinite)),
    documents,
    codes,
    segments,
  };
}
